// UX116 (docs/build-briefs/UX116-function-hubs.md; design/mockups/function-hubs/): draws the summary tiles on the
// three function hubs (Inventory, Ordering, Shift and closeout) in #function-hub-screen. The buttons on a
// hub are static markup owned by main.ts (permission gated by refreshAccountMenuRows, wired by the old row
// listeners); this module owns only the tiles. Reports is not a hub (RPTLAYOUT1-F1-S2): it opens the Reports home.
//
// Every figure is an existing read, shaped by functionHubsModel.ts. A read is made only when the session holds
// that read's permission (hasPermission, never a role name); a tile for a read it may not make, or one that
// failed, shows N/A and is not tappable. Nothing here writes, and nothing is remembered between visits:
// resetFunctionHubs wipes every figure at sign-out.

import {
  getCloseoutSummary,
  getDraftProposals,
  getReportLocations,
  listOpenTabs,
  listOpenTabSummaries,
  listPurchaseOrders,
  type CloseoutSummaryDto,
} from "./api.ts";
import type { MenuCountsBody } from "./accountMenu.ts";
import {
  HUB_IDS,
  NOT_AVAILABLE,
  approvalTile,
  cartTile,
  dayClosesTile,
  lastCloseoutTile,
  naList,
  naTile,
  netSalesTodayTile,
  openTicketsList,
  openTicketsListFromTabs,
  openTicketsTile,
  openTicketsTileFromTabs,
  type OpenTicketInput,
  shortDate,
  suggestedTile,
  toReceiveList,
  toReceiveTile,
  type HubId,
  type HubList,
  type HubTile,
} from "./functionHubsModel.ts";
import type { CountClosedSummary } from "./countTableModel.ts";
import { renderInventoryHome, resetInventoryHome, type InventoryHomeTarget } from "./inventoryHome.ts";
import { purchaseOrderIsReceivable } from "./purchasingDisplay.ts";
import { addDays } from "./reportFormat.ts";
import { hasPermission, type Session } from "./session.ts";

/** Where tapping a tile goes. main.ts resolves each to the same navigation the old menu row used. */
export type HubTarget =
  | {
      readonly kind: "view";
      readonly view: "stockLevels" | "counts";
      /** INVPOWALK1 (F9): a tile that counts something opens the list already filtered to what it counted. */
      readonly stockFilter?: "low" | "86";
    }
  | { readonly kind: "cart" }
  | { readonly kind: "orders" }
  | { readonly kind: "closeout"; readonly date?: string };

export interface FunctionHubDeps {
  /** INVDESIGN1-S2: where the Inventory home's buttons go (the stock levels, the counts list, the cart, the orders, Settings). */
  openInventoryTarget(target: InventoryHomeTarget): void;
  /** INVDESIGN1-S2: Count, Resume or Recount from the Inventory home. */
  startCount(options: { areaId?: string | null }): void;
  /** The tenant's current business date, from the server. Undefined when it could not be read. */
  businessDate(): Promise<string | undefined>;
  /** One fresh read of the menu counts, then the counts as they stand. */
  menuCounts(): Promise<MenuCountsBody>;
  locationName(): string | undefined;
  openTarget(target: HubTarget): void;
}

let deps: FunctionHubDeps | undefined;
/** Bumped on every open, chip change and reset: a response that finds it changed is dropped. */
let generation = 0;

export function configureFunctionHubs(next: FunctionHubDeps): void {
  deps = next;
}

function screenEl(): HTMLElement {
  const element = document.getElementById("function-hub-screen");
  if (element === null) throw new Error("function-hub-screen is missing from the page");
  return element;
}

function hubEl(hub: HubId): HTMLElement {
  const element = document.getElementById(`function-hub-${hub}`);
  if (element === null) throw new Error(`function-hub-${hub} is missing from the page`);
  return element;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// ---------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------

function drawTile(
  hub: HubId,
  tile: HubTile | undefined,
  label: string,
  id: string,
  onOpen: () => void,
): HTMLElement {
  // Not read yet: the frame with its label and no figure, so the grid does not jump when the figure lands.
  const settled = tile !== undefined;
  const live = settled && tile.available;
  const node = live ? el("button", "hub-tile") : el("div", "hub-tile hub-tile-na");
  if (node instanceof HTMLButtonElement) {
    node.type = "button";
    node.addEventListener("click", onOpen);
  }
  node.dataset["testid"] = `hub-tile-${id}`;
  node.dataset["tileId"] = id;
  node.dataset["hub"] = hub;
  if (!settled) node.setAttribute("aria-busy", "true");
  if (settled && tile.tone !== "plain") node.classList.add(`hub-tile-${tile.tone}`);
  if (live) node.append(el("span", "hub-tile-go"));
  const body = el("span", "hub-tile-body");
  body.append(el("span", "hub-tile-k", label));
  const value = el("span", "hub-tile-v", settled ? tile.value : "");
  value.dataset["testid"] = `hub-tile-${id}-value`;
  body.append(value);
  node.append(body);
  if (settled && tile.sub !== "") {
    const sub = el("span", "hub-tile-s");
    if (tile.delta !== undefined) {
      sub.append(el("span", `hub-delta hub-delta-${tile.delta.direction}`, tile.delta.text), " ");
    }
    sub.append(tile.sub);
    sub.dataset["testid"] = `hub-tile-${id}-sub`;
    node.append(sub);
  }
  return node;
}

function drawList(
  hub: HubId,
  list: HubList | undefined,
  label: string,
  id: string,
  onOpen: () => void,
): HTMLElement {
  const settled = list !== undefined;
  const live = settled && list.available;
  const node = live
    ? el("button", "hub-tile hub-list")
    : el("div", "hub-tile hub-list hub-tile-na");
  if (node instanceof HTMLButtonElement) {
    node.type = "button";
    node.addEventListener("click", onOpen);
  }
  node.dataset["testid"] = `hub-list-${id}`;
  node.dataset["hub"] = hub;
  if (!settled) node.setAttribute("aria-busy", "true");
  if (live) node.append(el("span", "hub-tile-go"));
  node.append(el("span", "hub-tile-k", label));
  if (settled && !list.available) {
    node.append(el("span", "hub-tile-v hub-list-na", NOT_AVAILABLE));
  }
  if (settled && list.available && list.rows.length === 0) {
    node.append(el("span", "hub-row hub-row-empty", "Nothing to show yet."));
  }
  if (settled) {
    for (const row of list.rows) {
      const line = el("span", "hub-row");
      line.dataset["testid"] = `hub-list-${id}-row`;
      const name = el("span", "hub-row-name");
      name.append(el("span", "hub-row-text", row.name));
      if (row.pill !== undefined) {
        name.append(el("span", `hub-pill hub-pill-${row.pill.tone}`, row.pill.text));
      }
      if (row.meta !== undefined) name.append(el("span", "hub-row-meta", row.meta));
      line.append(name, el("span", "hub-row-right", row.right));
      node.append(line);
    }
    // LOCFLOOR1 (sweep B5): a list cut short says how many there are and that the whole card opens the full list.
    if (list.available && list.viewAll !== undefined) {
      const more = el("span", "hub-row hub-row-viewall");
      more.dataset["testid"] = `hub-list-${id}-view-all`;
      more.append(el("span", "hub-row-text", list.viewAll));
      node.append(more);
    }
  }
  return node;
}

interface TileSpec {
  readonly id: string;
  readonly label: string;
  readonly target: HubTarget;
}
interface ListSpec extends TileSpec {
  readonly wide?: boolean;
}

/** The frames each hub draws before its reads land, in order: the tile grid and the lower grid. */
const HUB_FRAMES: Readonly<
  Record<
    HubId,
    { tiles: ReadonlyArray<TileSpec | ListSpec>; lower: ReadonlyArray<TileSpec | ListSpec> }
  >
> = {
  // INVDESIGN1-S2: the Inventory home is drawn by inventoryHome.ts, not by tiles.
  inventory: { tiles: [], lower: [] },
  ordering: {
    tiles: [
      { id: "cart", label: "In cart", target: { kind: "cart" } },
      { id: "approval", label: "Waiting approval", target: { kind: "orders" } },
      { id: "to-receive", label: "To receive", target: { kind: "orders" } },
      { id: "suggested", label: "Suggested", target: { kind: "cart" } },
    ],
    lower: [{ id: "to-receive-list", label: "Arriving soon", target: { kind: "orders" }, wide: true }],
  },
  shift: {
    tiles: [
      { id: "open-tickets", label: "Open tickets", target: { kind: "closeout" } },
      { id: "net-sales-today", label: "Net sales today", target: { kind: "closeout" } },
      { id: "day-closes", label: "Day closes", target: { kind: "closeout" } },
      { id: "last-closeout", label: "Last closeout", target: { kind: "closeout" } },
    ],
    lower: [
      { id: "open-tickets-list", label: "Open tickets", target: { kind: "closeout" }, wide: true },
    ],
  },
};

const LIST_IDS: ReadonlySet<string> = new Set([
  "needs-attention",
  "to-receive-list",
  "open-tickets-list",
]);

interface Figures {
  readonly tiles: ReadonlyMap<string, HubTile>;
  readonly lists: ReadonlyMap<string, HubList>;
  readonly targets?: ReadonlyMap<string, HubTarget>;
}

/** LOUNGERMAJ1 (lounger-14): the Inventory tiles this session's role cannot read at all. They are not drawn, so a lounger
 * does not see six dashes. A tile the role may read that failed to load still draws as N/A. */
function unreadableTiles(session: Session, hub: HubId): ReadonlySet<string> {
  const hidden = new Set<string>();
  if (hub !== "inventory") return hidden;
  const may = (id: string): boolean => hasPermission(session, id);
  if (!may("report.stock_value.view")) {
    for (const id of ["low", "out", "stock-value", "needs-attention"]) hidden.add(id);
  }
  if (!may("inv.stock_flag.view")) hidden.add("flagged");
  if (!may("inv.count_session.run")) hidden.add("last-count");
  return hidden;
}

function render(hub: HubId, figures: Figures | undefined, hidden: ReadonlySet<string>): void {
  const root = hubEl(hub);
  const frames = HUB_FRAMES[hub];
  const fill = (container: HTMLElement | null, specs: ReadonlyArray<TileSpec | ListSpec>): void => {
    if (container === null) return;
    const nodes = specs.filter((spec) => !hidden.has(spec.id)).map((spec) => {
      const target = figures?.targets?.get(spec.id) ?? spec.target;
      const open = (): void => deps?.openTarget(target);
      const node = LIST_IDS.has(spec.id)
        ? drawList(hub, figures?.lists.get(spec.id), spec.label, spec.id, open)
        : drawTile(hub, figures?.tiles.get(spec.id), spec.label, spec.id, open);
      if ("wide" in spec && spec.wide === true) node.classList.add("hub-wide");
      return node;
    });
    container.replaceChildren(...nodes);
  };
  fill(root.querySelector<HTMLElement>("[data-hub-tiles]"), frames.tiles);
  fill(root.querySelector<HTMLElement>("[data-hub-lower]"), frames.lower);
}

// ---------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------

function ok<T extends object>(result: { status: number; body: T }): T | undefined {
  return result.status === 200 ? result.body : undefined;
}

function pack(
  tiles: ReadonlyArray<HubTile>,
  lists: ReadonlyArray<HubList>,
  targets: ReadonlyArray<readonly [string, HubTarget]> = [],
): Figures {
  return {
    tiles: new Map(tiles.map((tile) => [tile.id, tile])),
    lists: new Map(lists.map((list) => [list.id, list])),
    targets: new Map(targets),
  };
}

async function readOrdering(session: Session): Promise<Figures> {
  const may = (id: string): boolean => hasPermission(session, id);
  const base = session.apiBaseUrl;
  const tenant = session.tenantId;
  const [counts, orders, proposals] = await Promise.all([
    deps === undefined ? Promise.resolve<MenuCountsBody>({}) : deps.menuCounts(),
    may("purchasing.order.view") ? listPurchaseOrders(base, tenant).then(ok) : undefined,
    may("purchasing.cart.manage") ? getDraftProposals(base, tenant).then(ok) : undefined,
  ]);
  const cart = may("purchasing.cart.manage") ? counts.cartLines : undefined;
  const waiting = may("purchasing.order.view") ? counts.ordersAwaitingApproval : undefined;
  return pack(
    [
      cartTile(cart),
      approvalTile(waiting),
      toReceiveTile(orders?.orders, purchaseOrderIsReceivable),
      suggestedTile(proposals?.proposals.length),
    ],
    [toReceiveList(orders?.orders, purchaseOrderIsReceivable)],
  );
}

async function readShift(session: Session, today: string | undefined): Promise<Figures> {
  const may = (id: string): boolean => hasPermission(session, id);
  const base = session.apiBaseUrl;
  const tenant = session.tenantId;
  let summary: CloseoutSummaryDto | undefined;
  let yesterday: CloseoutSummaryDto | undefined;
  const yesterdayDate = today === undefined ? "" : addDays(today, -1);
  if (today !== undefined && may("report.closeout.view")) {
    // The closeout screen reads one location's day; this reads the first location it would open on.
    const locations = ok(await getReportLocations(base, tenant));
    const locationId = locations?.locations[0]?.id;
    if (locationId !== undefined) {
      [summary, yesterday] = await Promise.all([
        getCloseoutSummary(base, tenant, today, locationId).then(ok),
        getCloseoutSummary(base, tenant, yesterdayDate, locationId).then(ok),
      ]);
    }
  }
  // MGRDAYMAJ1 (manager-day-11): the tickets still open at this location, whatever day they were opened. The closeout read above
  // lists one business day's tabs only, so it stays the fallback when the Open Tabs read cannot be made.
  let everyOpen: OpenTicketInput[] | undefined;
  // Still a closeout figure: a person with no closeout permission sees N/A here, whatever else they may read.
  if (today !== undefined && may("report.closeout.view") && may("pos.tab.view_all")) {
    const [tabs, summaries] = await Promise.all([
      listOpenTabs(base, tenant).then(ok),
      listOpenTabSummaries(base, tenant).then(ok),
    ]);
    if (tabs !== undefined && summaries !== undefined) {
      const figures = new Map(summaries.summaries.map((entry) => [entry.orderId, entry]));
      everyOpen = tabs.tabs.map((tab) => ({
        orderId: tab.id,
        name: tab.name,
        createdAt: tab.created_at,
        businessDate: tab.business_date,
        drinkCount: figures.get(tab.id)?.drinkCount ?? 0,
        owedWithTaxCents: figures.get(tab.id)?.owedWithTaxCents,
      }));
    }
  }
  const openTile =
    everyOpen !== undefined && today !== undefined
      ? openTicketsTileFromTabs(everyOpen, today)
      : openTicketsTile(summary);
  const openList =
    everyOpen !== undefined ? openTicketsListFromTabs(everyOpen, Date.now()) : openTicketsList(summary, Date.now());
  return pack(
    [
      openTile,
      netSalesTodayTile(summary),
      dayClosesTile(summary),
      lastCloseoutTile(yesterday, yesterdayDate),
    ],
    [openList],
    [["last-closeout", { kind: "closeout", date: yesterdayDate }]],
  );
}

function naFigures(hub: HubId): Figures {
  const frames = HUB_FRAMES[hub];
  const all = [...frames.tiles, ...frames.lower];
  return pack(
    all.filter((spec) => !LIST_IDS.has(spec.id)).map((spec) => naTile(spec.id, spec.label)),
    all.filter((spec) => LIST_IDS.has(spec.id)).map((spec) => naList(spec.id, spec.label)),
  );
}

/** Set by showInventoryAfterCount, consumed by the next open of the Inventory hub. */
let pendingAfterCount: CountClosedSummary | undefined;

async function drawInventory(
  session: Session,
  gen: number,
  afterCount: CountClosedSummary | undefined,
): Promise<void> {
  const host = hubEl("inventory").querySelector<HTMLElement>("[data-inv-home]");
  if (host === null || deps === undefined) return;
  await renderInventoryHome(
    session,
    host,
    {
      openTarget: (target) => deps?.openInventoryTarget(target),
      startCount: (options) => deps?.startCount(options),
      now: () => Date.now(),
    },
    afterCount,
    () => gen === generation,
  );
}

async function load(session: Session, hub: HubId): Promise<void> {
  const gen = ++generation;
  const hidden = unreadableTiles(session, hub);
  render(hub, undefined, hidden);
  let figures: Figures;
  try {
    const today = hub === "shift" ? await deps?.businessDate() : undefined;
    if (gen !== generation) return;
    switch (hub) {
      case "inventory":
        // INVDESIGN1-S2: the home draws itself (and the after-count screen); it has no tiles to fill.
        await drawInventory(session, gen, pendingAfterCount);
        return;
      case "ordering":
        figures = await readOrdering(session);
        break;
      case "shift":
        figures = await readShift(session, today);
        if (today !== undefined) setBusinessDay(today);
        break;
    }
  } catch {
    // A read that threw is a tile that cannot say: N/A, never a stale or invented figure.
    figures = naFigures(hub);
  }
  if (gen !== generation) return;
  render(hub, figures, hidden);
}

function setBusinessDay(today: string): void {
  const chip = hubEl("shift").querySelector<HTMLElement>('[data-testid="hub-business-day"]');
  if (chip === null) return;
  chip.textContent = `Business day ${shortDate(today)}`;
  chip.hidden = false;
}

// ---------------------------------------------------------------------
// Entry points
// ---------------------------------------------------------------------

/** Shows one hub and reads its figures. */
export function openHub(session: Session, hub: HubId, options: { afterCount?: CountClosedSummary } = {}): void {
  // INVDESIGN1-S2: a count just closed on this open shows "Count closed" and what to order; any other open shows the home.
  pendingAfterCount = hub === "inventory" ? options.afterCount : undefined;
  screenEl();
  for (const id of HUB_IDS) hubEl(id).hidden = id !== hub;
  const location = deps?.locationName();
  const eyebrow = hubEl(hub).querySelector<HTMLElement>("[data-hub-location]");
  if (eyebrow !== null) {
    eyebrow.textContent = location ?? "";
    eyebrow.hidden = location === undefined || location === "";
  }
  void load(session, hub);
}

/** Sign-out and every other end of a session: wipes every figure and drops reads still in flight. */
export function resetFunctionHubs(): void {
  generation += 1;
  const screen = document.getElementById("function-hub-screen");
  if (screen === null) return;
  for (const container of Array.from(
    screen.querySelectorAll<HTMLElement>("[data-hub-tiles], [data-hub-lower]"),
  )) {
    container.replaceChildren();
  }
  for (const id of HUB_IDS) {
    const hub = document.getElementById(`function-hub-${id}`);
    if (hub !== null) hub.hidden = true;
  }
  resetInventoryHome();
  const chip = document.querySelector<HTMLElement>('[data-testid="hub-business-day"]');
  if (chip !== null) {
    chip.textContent = "";
    chip.hidden = true;
  }
}
